import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
	Button,
	Div,
	InputGroup,
	Panel,
	Typography,
} from '@icecreamsocial/components';
import { useFormik } from 'formik';
import {
	DeleteIcon,
	EnhancedForm,
	ImageUploader,
	RequiredAsterisk,
} from '../../../../presentational';
import NonSquareOffers from './NonSquareOffers';
import SquareOffers from './SquareOffers';

const OfferSettings = ({
	giftCardAmount = 0,
	refundAmount = 0,
	conversionGoal = 5,
	offerHeading = undefined,
	offerInstructions = undefined,
	offerImageCaption = undefined,
	offerImageTitle = undefined,
	offerImage = undefined,
	thankYouImage = undefined,
	isSquare = false,
	hasProcessor = false,
	allowRouteBlock = true,
	renderControls = null,
	onSubmit,
}) => {
	/** hidden inputs controls */
	const [showOfferImageCaptionField, setShowOfferImageCaption] = useState(
		!!offerImageCaption
	);
	const [showOfferImageTitleField, setOfferImageTitleField] = useState(
		!!offerImageTitle
	);
	// toggle open or close on updates
	useEffect(() => {
		setShowOfferImageCaption(!!offerImageCaption);
		setOfferImageTitleField(!!offerImageTitle);
	}, [offerImageCaption, offerImageTitle]);

	/**
	 * Recommended/example texts
	 */
	const recommendedOfferHeading = isSquare
		? `Invite Your Friends & Get MONEY BACK!`
		: `Invite Your Friends & Get a 50% REFUND!`;
	const recommendedOfferInstructions = isSquare
		? `💪 Simply invite your friends by sending them this. When a friend buys, we'll give you a $10 credit, plus they’ll get 10% off their purchase! Thanks for sharing! 🙌`
		: `💪 Simply invite your friends by sending them this. Once 5 Friends buy, we will give you a 50% REFUND, plus they’ll get 10% off their purchase! Thanks for sharing! 🙌`;

	/**
	 * default values
	 */
	const defaultHeading = !offerHeading ? recommendedOfferHeading : offerHeading;

	const defaultInstructions = !offerInstructions
		? recommendedOfferInstructions
		: offerInstructions;

	const formikProps = {
		enableReinitialize: true,

		initialValues: {
			offerHeading: defaultHeading,
			offerInstructions: defaultInstructions,
			giftCardAmount,
			conversionGoal,
			offerImageCaption,
			offerImageTitle,
			offerImage,
			thankYouImage,
			refundAmount,
		},
		validate: (values) => {
			const ONE_MB = 1024 * 1024; // 1 MB
			const errors = {};

			if (!values.thankYouImage) {
				errors.thankYouImage = 'Please upload a Thank You Image';
			}
			if (!values.offerImage) {
				errors.offerImage = 'Please upload an Offer Image';
			}

			if (values.thankYouImage?.size > ONE_MB) {
				errors.thankYouImage = `Your image has exceeded the maximum file size of 1MB. (Size: ${(
					values.thankYouImage?.size / ONE_MB || 0
				).toFixed(2)}MB )`;
			}
			if (values.offerImage?.size > ONE_MB) {
				errors.offerImage = `Your image has exceeded the maximum file size of 1MB. (Size: ${(
					values.offerImage?.size / ONE_MB || 0
				).toFixed(2)}MB )`;
			}

			return errors;
		},
		onSubmit,
	};

	/**
	 * Offer
	 */

	return (
		<EnhancedForm
			formikProps={formikProps}
			promptIf={({ dirty }) => allowRouteBlock && dirty}
			renderControls={renderControls}
		>
			{(offerSettings) => (
				<>
					<Panel backgroundColor="bodyInverse" marginBottom="lg">
						<Panel.Header
							backgroundColor="bodyInverse"
							textColor="body"
							paddingBottom="0"
						>
							<Typography.H2
								textColor="body"
								textAlign="center"
								margin="none"
								typoSize="md"
							>
								Reward
							</Typography.H2>
							<Typography.P marginBottom="0">
								After an influencer reaches their conversion goal, how will they
								be rewarded?
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							{isSquare ? (
								<SquareOffers
									value={offerSettings.values.giftCardAmount}
									handleChange={offerSettings.handleChange}
								/>
							) : (
								<NonSquareOffers
									disableCredit={!hasProcessor}
									refundAmount={offerSettings.values.refundAmount}
									handleChange={offerSettings.handleChange}
								/>
							)}
						</Panel.Body>
					</Panel>
					<Panel backgroundColor="bodyInverse" marginBottom="lg">
						<Panel.Header
							backgroundColor="bodyInverse"
							textColor="body"
							paddingBottom="0"
						>
							<Typography.H2
								textColor="body"
								textAlign="center"
								margin="none"
								typoSize="md"
							>
								Conversion Goal
							</Typography.H2>
							<Typography.P marginBottom="0">
								To earn their reward, an influencer must get this many friends
								to take your desired action
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<InputGroup marginBottom="sm">
								<InputGroup.Label>
									Conversion Goal <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight="md">
									<InputGroup.Input
										id="conversionGoal"
										name="conversionGoal"
										paddingLeft="md"
										value={offerSettings.values.conversionGoal}
										onChange={offerSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup>
						</Panel.Body>
					</Panel>
					<Panel backgroundColor="bodyInverse" marginBottom="lg">
						<Panel.Header
							backgroundColor="bodyInverse"
							textColor="body"
							paddingBottom="0"
							grid
							gridColumns="repeat(3, 1fr)"
							alignItems="center"
						>
							<Typography.H2
								textColor="body"
								textAlign="center"
								margin="none"
								typoSize="md"
								gridColumn="2"
							>
								Campaign Offer Details
							</Typography.H2>
							<Button
								type="button"
								textDecoration={
									offerSettings.values.offerHeading !==
										recommendedOfferHeading ||
									offerSettings.values.offerInstructions !==
										recommendedOfferInstructions
										? 'underline'
										: undefined
								}
								textColor={
									offerSettings.values.offerHeading !==
										recommendedOfferHeading ||
									offerSettings.values.offerInstructions !==
										recommendedOfferInstructions
										? 'link'
										: 'ambient'
								}
								onClick={() => {
									offerSettings.setFieldValue(
										'offerHeading',
										recommendedOfferHeading
									);
									offerSettings.setFieldValue(
										'offerInstructions',
										recommendedOfferInstructions
									);
								}}
								backgroundColor="transparent"
								shadow="none"
								hoverShadow="none"
								border="none"
								padding="none"
							>
								Use Recommended Text
							</Button>
						</Panel.Header>
						<Panel.Body>
							<InputGroup marginBottom="sm" elWidth="100%">
								<InputGroup.Label>
									Offer Header <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight="md">
									<InputGroup.Input
										id="offerHeading"
										name="offerHeading"
										paddingLeft="md"
										value={offerSettings.values.offerHeading}
										onChange={offerSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup>
							<InputGroup elWidth="100%">
								<InputGroup.Label>
									Offer Instructions <RequiredAsterisk />
								</InputGroup.Label>
								<InputGroup.Fields marginRight="md">
									<InputGroup.Input
										as="textarea"
										id="offerInstructions"
										name="offerInstructions"
										paddingLeft="md"
										rows="5"
										value={offerSettings.values.offerInstructions}
										onChange={offerSettings.handleChange}
										required
									/>
								</InputGroup.Fields>
							</InputGroup>
						</Panel.Body>
					</Panel>
					<Panel backgroundColor="bodyInverse" marginBottom="lg">
						<Panel.Header
							backgroundColor="bodyInverse"
							textColor="body"
							paddingBottom="0"
						>
							<Typography.H2
								textColor="body"
								textAlign="center"
								margin="none"
								typoSize="md"
							>
								Offer Image <RequiredAsterisk />
							</Typography.H2>
							<Typography.P marginBottom="0">
								This image will be the focal point of the offer and draw people
								in. Before uploading an image, please review our Image
								Guidelines. Violating the guidelines will result in a disruption
								of service.
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<Div marginBottom="md">
								{!showOfferImageTitleField && (
									<Button
										type="button"
										onClick={() => setOfferImageTitleField(true)}
										textDecoration="underline"
										textColor="link"
										backgroundColor="transparent"
										padding="0"
										border="0"
										hoverShadow="none"
										shadow="none"
									>
										+ Add Image Title
									</Button>
								)}
								{!showOfferImageTitleField && !showOfferImageCaptionField && (
									<Typography.Span margin="0 8px" textColor="ambient">
										|
									</Typography.Span>
								)}
								{!showOfferImageCaptionField && (
									<Button
										type="button"
										onClick={() => setShowOfferImageCaption(true)}
										textDecoration="underline"
										textColor="link"
										backgroundColor="transparent"
										padding="0"
										border="0"
										hoverShadow="none"
										shadow="none"
									>
										+ Add Image Caption
									</Button>
								)}
								{showOfferImageTitleField && (
									<InputGroup marginTop="sm" elWidth="100%">
										<InputGroup.Label>Offer Image Title</InputGroup.Label>
										<InputGroup.Fields marginRight="md">
											<InputGroup.Input
												id="offerImageTitle"
												name="offerImageTitle"
												paddingLeft="md"
												value={offerSettings.values.offerImageTitle}
												onChange={offerSettings.handleChange}
											/>
											<InputGroup.Actions display="flex" alignItems="center">
												<InputGroup.Action
													type="button"
													onClick={() => {
														if (offerSettings.values.offerImageTitle) {
															offerSettings.setFieldValue(
																'offerImageTitle',
																''
															);
														}
													}}
												>
													<DeleteIcon textColor="ambientDark" />
												</InputGroup.Action>
											</InputGroup.Actions>
										</InputGroup.Fields>
									</InputGroup>
								)}
								{showOfferImageCaptionField && (
									<InputGroup marginTop="sm" elWidth="100%">
										<InputGroup.Label>Offer Image Caption</InputGroup.Label>
										<InputGroup.Fields marginRight="md">
											<InputGroup.Input
												id="offerImageCaption"
												name="offerImageCaption"
												paddingLeft="md"
												value={offerSettings.values.offerImageCaption}
												onChange={offerSettings.handleChange}
											/>
											<InputGroup.Actions display="flex" alignItems="center">
												<InputGroup.Action
													type="button"
													onClick={() => {
														if (offerSettings.values.offerImageCaption) {
															offerSettings.setFieldValue(
																'offerImageCaption',
																''
															);
														}
													}}
												>
													<DeleteIcon textColor="ambientDark" />
												</InputGroup.Action>
											</InputGroup.Actions>
										</InputGroup.Fields>
									</InputGroup>
								)}
							</Div>
							<ImageUploader
								name="offerImage"
								value={offerSettings.values.offerImage}
								handleChange={(e) =>
									offerSettings.setFieldValue(
										'offerImage',
										e.currentTarget.files[0]
									)
								}
								onReplace={(e) => {
									e.preventDefault();
									offerSettings.setFieldValue('offerImage', undefined);
								}}
								required={true}
							/>
							{offerSettings.errors.offerImage && (
								<Typography.P textColor="bad">
									{offerSettings.errors.offerImage}
								</Typography.P>
							)}
						</Panel.Body>
					</Panel>
					<Panel backgroundColor="bodyInverse" marginBottom="lg">
						<Panel.Header
							backgroundColor="bodyInverse"
							textColor="body"
							paddingBottom="0"
						>
							<Typography.H2
								textColor="body"
								textAlign="center"
								margin="none"
								typoSize="md"
							>
								Thank You Image <RequiredAsterisk />
							</Typography.H2>
							<Typography.P marginBottom="0">
								This image will appear after the customer sends out invites.
								Before uploading an image, please review our Image Guidelines.
								Violating the guidelines will result in a disruption of service.
							</Typography.P>
						</Panel.Header>
						<Panel.Body>
							<ImageUploader
								name="thankYouImage"
								value={offerSettings.values.thankYouImage}
								handleChange={(e) =>
									offerSettings.setFieldValue(
										'thankYouImage',
										e.currentTarget.files[0]
									)
								}
								onReplace={(e) => {
									e.preventDefault();
									offerSettings.setFieldValue('thankYouImage', undefined);
								}}
								required={true}
							/>
							{offerSettings.errors.thankYouImage && (
								<Typography.P textColor="bad">
									{offerSettings.errors.thankYouImage}
								</Typography.P>
							)}
						</Panel.Body>
					</Panel>
				</>
			)}
		</EnhancedForm>
	);
};
OfferSettings.displayName = 'OfferSettingsForm';

export default observer(OfferSettings);
